import { FolderWithMeta, FileUpload } from '../../api';

type DeleteTarget =
  | { type: 'folder'; item: FolderWithMeta }
  | { type: 'file'; item: FileUpload }
  | null;

interface BulkDeleteTarget {
  type: 'files' | 'folders';
  ids: string[];
}

interface DeleteConfirmModalsProps {
  deleteTarget: DeleteTarget;
  /** True while a single folder/file delete request is in flight. */
  deleting: boolean;
  onCancelDelete: () => void;
  onDeleteFolder: () => void;
  onDeleteFile: () => void;
  bulkDeleteTarget: BulkDeleteTarget | null;
  bulkDeleting: boolean;
  /** True once the user has asked to stop an in-flight bulk delete (shows "Stopping…"). */
  bulkDeleteCancelling: boolean;
  bulkDeleteProgress: { current: number; total: number };
  /** Closes the dialog when idle; requests a stop when a bulk delete is in progress. */
  onCancelBulkDelete: () => void;
  onExecuteBulkDelete: () => void;
}

export function DeleteConfirmModals({
  deleteTarget,
  deleting,
  onCancelDelete,
  onDeleteFolder,
  onDeleteFile,
  bulkDeleteTarget,
  bulkDeleting,
  bulkDeleteCancelling,
  bulkDeleteProgress,
  onCancelBulkDelete,
  onExecuteBulkDelete,
}: DeleteConfirmModalsProps) {
  const folder = deleteTarget?.type === 'folder' ? deleteTarget.item : null;
  // Only empty folders can be deleted. Build a human-readable description of
  // what's blocking the delete so the dialog can ask the user to empty it first.
  const folderContents: string[] = [];
  if (folder && folder.subfolder_count > 0) {
    folderContents.push(`${folder.subfolder_count} subfolder(s)`);
  }
  if (folder && folder.file_count > 0) {
    folderContents.push(`${folder.file_count} file(s)`);
  }
  const folderIsEmpty = folderContents.length === 0;

  return (
    <>
      {/* Delete confirmation - Folder */}
      {folder && (
        <div className="modal-overlay" onClick={() => !deleting && onCancelDelete()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Folder</h3>
            {folderIsEmpty ? (
              <>
                <p>Are you sure you want to delete "{folder.name}"?</p>
                <p className="delete-warning">This action cannot be undone.</p>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={onCancelDelete} disabled={deleting}>
                    Cancel
                  </button>
                  <button className="btn-danger" onClick={onDeleteFolder} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="delete-error">"{folder.name}" isn't empty, so it can't be deleted.</p>
                <p>
                  It contains {folderContents.join(' and ')}. Please move or delete its contents first,
                  then delete the folder.
                </p>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={onCancelDelete}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation - File */}
      {deleteTarget?.type === 'file' && (
        <div className="modal-overlay" onClick={() => !deleting && onCancelDelete()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete File</h3>
            <p>Are you sure you want to delete "{deleteTarget.item.file_name}"?</p>
            <p className="delete-warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={onCancelDelete} disabled={deleting}>
                Cancel
              </button>
              <button className="btn-danger" onClick={onDeleteFile} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation */}
      {bulkDeleteTarget && (
        <div className="modal-overlay" onClick={() => !bulkDeleting && onCancelBulkDelete()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Delete {bulkDeleteTarget.type === 'files' ? 'Files' : 'Folders'}
            </h3>
            {bulkDeleting ? (
              <p>
                {bulkDeleteCancelling ? 'Stopping… ' : 'Deleting '}
                {bulkDeleteProgress.current} of {bulkDeleteProgress.total}…
              </p>
            ) : (
              <>
                <p>
                  Are you sure you want to delete {bulkDeleteTarget.ids.length}{' '}
                  {bulkDeleteTarget.type === 'files'
                    ? bulkDeleteTarget.ids.length === 1 ? 'file' : 'files'
                    : bulkDeleteTarget.ids.length === 1 ? 'folder' : 'folders'}?
                </p>
                {bulkDeleteTarget.type === 'folders' && (
                  <p className="delete-warning">
                    Only empty folders can be deleted. Any selected folder that still contains files or
                    subfolders will be skipped.
                  </p>
                )}
                <p className="delete-warning">This action cannot be undone.</p>
              </>
            )}
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={onCancelBulkDelete}
                disabled={bulkDeleteCancelling}
              >
                {bulkDeleteCancelling ? 'Stopping…' : 'Cancel'}
              </button>
              <button
                className="btn-danger"
                onClick={onExecuteBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting
                  ? `Deleting (${bulkDeleteProgress.current}/${bulkDeleteProgress.total})...`
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
